/**
 * The Roadmap Schedule Gantt's day calendar (owner request 2026-11-03: break the Gantt down by
 * day, showing weekends and non-working days).
 *
 * The holiday list MIRRORS the scheduler's own calendar,
 * `apps/portal/server/src/program_planning/utils/businessDayCalendar.ts` (getUSFederalHolidays),
 * rule for rule — so a day shaded here is exactly a day `addBusinessDays` skips when it lays out
 * the phases. Change one, change the other.
 */

function nthWeekday(year: number, month: number, weekday: number, n: number): Date {
  const firstWeekday = new Date(year, month, 1).getDay();
  return new Date(year, month, 1 + ((weekday - firstWeekday + 7) % 7) + (n - 1) * 7);
}

function lastWeekday(year: number, month: number, weekday: number): Date {
  const last = new Date(year, month + 1, 0);
  return new Date(year, month, last.getDate() - ((last.getDay() - weekday + 7) % 7));
}

function usFederalHolidays(year: number): Array<{ date: Date; name: string }> {
  return [
    { date: new Date(year, 0, 1), name: "New Year's Day" },
    { date: new Date(year, 5, 19), name: 'Juneteenth' },
    { date: new Date(year, 6, 4), name: 'Independence Day' },
    { date: new Date(year, 10, 11), name: 'Veterans Day' },
    { date: new Date(year, 11, 25), name: 'Christmas Day' },
    { date: nthWeekday(year, 0, 1, 3), name: 'Martin Luther King Jr. Day' },
    { date: nthWeekday(year, 1, 1, 3), name: "Presidents' Day" },
    { date: lastWeekday(year, 4, 1), name: 'Memorial Day' },
    { date: nthWeekday(year, 8, 1, 1), name: 'Labor Day' },
    { date: nthWeekday(year, 9, 1, 2), name: 'Columbus Day' },
    { date: nthWeekday(year, 10, 4, 4), name: 'Thanksgiving Day' },
  ];
}

export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** A schedule date ("2026-11-02" or "2026-11-02T00:00:00.000Z") as a LOCAL calendar day. */
export function parseDay(value: string): Date {
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  return new Date(y!, m! - 1, d!);
}

/** Whole calendar days from a to b (DST-safe: counted on local dates, rounded). */
export function daysBetween(a: Date, b: Date): number {
  return Math.round((new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime()
    - new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime()) / 86400000);
}

/** Holiday name by day key, for every year the range touches. */
export function holidaysBetween(start: Date, end: Date): Map<string, string> {
  const out = new Map<string, string>();
  for (let y = start.getFullYear(); y <= end.getFullYear(); y++) {
    for (const h of usFederalHolidays(y)) out.set(dayKey(h.date), h.name);
  }
  return out;
}
